import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { copyShareLink } from '@/lib/generation';
import {
  Film, ImageIcon, Trash2, Video, ChevronRight, Check, Pencil, Camera, Wand2, MapPin, Link2, CopyPlus, BadgeCheck, Play, X, Loader2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

function getSceneStage(scene) {
  if (scene.video_path) {
    return scene.video_approved
      ? { key: 'video-approved', label: 'Video Approved', color: '#22C55E', icon: BadgeCheck }
      : { key: 'video', label: 'Video Ready', color: '#10B981', icon: Check };
  }
  if (scene.dialogue_lines?.length > 0) return { key: 'dialogue', label: 'Dialogue Set', color: '#A78BFA', icon: Film };
  if (scene.first_frame_path || scene.last_frame_path) return { key: 'frames', label: 'Frames Ready', color: '#E879F9', icon: Camera };
  if (scene.start_action || scene.end_action) return { key: 'actions', label: 'Actions Set', color: '#00F0FF', icon: Wand2 };
  if (scene.location_ref_path || scene.character_scene_refs?.length > 0) return { key: 'refs', label: 'Refs Set', color: '#F59E0B', icon: MapPin };
  return { key: 'draft', label: 'Draft', color: '#8A8A93', icon: Pencil };
}

export function SceneCard({ scene, sceneNumber, projectId, characters, onDelete, onDuplicate, onUpdate }) {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [approving, setApproving] = useState(false);
  const toggleApprove = async (e) => {
    e.stopPropagation();
    setApproving(true);
    try {
      const res = await api.put(`/projects/${projectId}/scenes/${scene.id}`, { video_approved: !scene.video_approved });
      onUpdate?.(res.data);
      toast.success(res.data.video_approved ? 'Video approved' : 'Approval removed');
    } catch { toast.error('Could not update approval'); }
    finally { setApproving(false); }
  };
  const stage = getSceneStage(scene);
  const StageIcon = stage.icon;

  const startChars = characters.filter(c => scene.start_character_ids?.includes(c.id));
  const endChars = characters.filter(c => scene.end_character_ids?.includes(c.id));

  return (
    <div
      data-testid={`scene-card-${scene.id}`}
      className="ff-card group cursor-pointer transition-all hover:border-white/10"
      onClick={() => navigate(`/project/${projectId}/scenes/${scene.id}`)}
    >
      <div className="flex gap-4 p-4">
        {/* Thumbnail */}
        <div className="w-40 h-24 rounded-lg overflow-hidden bg-[#0a0a0e] border border-white/[0.04] shrink-0 relative">
          {scene.first_frame_path ? (
            <img
              src={getImageUrl(scene.first_frame_path)}
              alt={scene.title}
              className="w-full h-full object-cover"
              data-testid={`scene-thumb-${scene.id}`}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-[#55555C]">
              <ImageIcon className="w-6 h-6 mb-1" />
              <span className="text-[10px]">No frame</span>
            </div>
          )}
          {scene.video_path && (
            <>
              <div className="absolute top-1.5 right-1.5 p-1 rounded bg-black/70">
                <Video className="w-3 h-3 text-[#E879F9]" />
              </div>
              <button data-testid={`scene-play-${scene.id}`} onClick={(e) => { e.stopPropagation(); setPlaying(true); }} title="Play video"
                className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="w-9 h-9 rounded-full bg-[#E879F9] text-white flex items-center justify-center shadow-lg"><Play className="w-4 h-4 ml-0.5" /></span>
              </button>
            </>
          )}
        </div>
        {playing && createPortal(
          <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4" data-testid={`scene-player-${scene.id}`} onClick={(e) => { e.stopPropagation(); setPlaying(false); }}>
            <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-white font-semibold">{sceneNumber}. {scene.title}</p>
                <div className="flex items-center gap-2">
                  <button data-testid={`scene-player-approve-${scene.id}`} onClick={toggleApprove} disabled={approving}
                    className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${scene.video_approved ? 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30' : 'bg-white/[0.06] text-[#c8c8d0] border-white/10 hover:text-white'}`}>
                    {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />} {scene.video_approved ? 'Approved' : 'Approve'}
                  </button>
                  <button onClick={() => setPlaying(false)} className="p-1.5 rounded hover:bg-white/10 text-[#8A8A93]"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <video src={getImageUrl(scene.video_path)} controls autoPlay className="w-full rounded-xl bg-black max-h-[75vh]" />
            </div>
          </div>, document.body)}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {sceneNumber != null && (
                <span data-testid={`scene-number-${scene.id}`} className="shrink-0 w-6 h-6 rounded-md bg-[#FF4500]/15 text-[#FF4500] text-xs font-bold flex items-center justify-center">{sceneNumber}</span>
              )}
              <Film className="w-4 h-4 text-[#FF4500] shrink-0" />
              <h3 className="text-sm font-semibold text-white truncate">{scene.title}</h3>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {scene.video_path && (
                <button data-testid={`scene-approve-${scene.id}`} onClick={toggleApprove} disabled={approving} title={scene.video_approved ? 'Remove approval' : 'Approve video'}
                  className={`p-1.5 rounded-lg transition-colors ${scene.video_approved ? 'text-[#22C55E] hover:bg-[#22C55E]/10' : 'text-[#55555C] hover:text-[#22C55E] hover:bg-[#22C55E]/10'}`}>
                  {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />}
                </button>
              )}
              <button
                data-testid={`copy-scene-link-${scene.id}`}
                onClick={(e) => { e.stopPropagation(); copyShareLink(`/project/${projectId}/scenes/${scene.id}`, 'Scene link'); }}
                title="Copy link to this scene"
                className="p-1.5 rounded-lg hover:bg-[#00F0FF]/10 text-[#55555C] hover:text-[#00F0FF] transition-colors opacity-0 group-hover:opacity-100"
              >
                <Link2 className="w-3.5 h-3.5" />
              </button>
              {onDuplicate && (
                <button
                  data-testid={`duplicate-scene-${scene.id}`}
                  onClick={(e) => { e.stopPropagation(); onDuplicate(scene.id); }}
                  title="Duplicate this scene"
                  className="p-1.5 rounded-lg hover:bg-[#E879F9]/10 text-[#55555C] hover:text-[#E879F9] transition-colors opacity-0 group-hover:opacity-100"
                >
                  <CopyPlus className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                data-testid={`delete-scene-${scene.id}`}
                onClick={(e) => { e.stopPropagation(); onDelete(scene.id); }}
                className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#55555C] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <ChevronRight className="w-4 h-4 text-[#55555C] group-hover:text-white transition-colors" />
            </div>
          </div>

          {/* Stage badge + metadata */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <Badge variant="outline" className="text-[10px] py-0 flex items-center gap-1" style={{ color: stage.color, borderColor: `${stage.color}33` }}>
              <StageIcon className="w-2.5 h-2.5" />{stage.label}
            </Badge>
            {scene.setting && <Badge variant="outline" className="text-[#8A8A93] border-white/10 text-[10px] py-0">{scene.setting}</Badge>}
            {scene.mood && <Badge variant="outline" className="text-[#8A8A93] border-white/10 text-[10px] py-0">{scene.mood}</Badge>}
          </div>

          {/* Characters */}
          {(startChars.length > 0 || endChars.length > 0) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {startChars.map(c => (
                <span key={`s-${c.id}`} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF4500]/10 text-[#FF4500]">@{c.name}</span>
              ))}
              {endChars.filter(c => !startChars.find(sc => sc.id === c.id)).map(c => (
                <span key={`e-${c.id}`} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF]">@{c.name}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
